/**
 * platform/lifecycle.ts — TURNING FOUR KINDS OF ABSENCE INTO FOUR SENTENCES.
 *
 * ===========================================================================
 * WHAT THIS MODULE IS FOR
 * ===========================================================================
 * Every screen in this console renders a record that is mostly nullable, and
 * the nulls do not mean the same thing:
 *
 *   WITHHELD           we hold it and policy forbids showing it
 *   ABSENT             no such record — the publisher named no edition, or
 *                      nothing has superseded this version yet
 *   ZERO               a real measured zero
 *   REFUSED-FOR-SCOPE  the caller may not ask
 *
 * A console that renders all four as an em-dash is the failure this codebase
 * has found nineteen times and counting. An operator reading a blank
 * `effectiveTo` cannot tell "this requirement is open-ended" from "we do not
 * know when it ends", and those lead to opposite decisions about whether to
 * retire it.
 *
 * So no screen formats a nullable field itself. Every one of them comes through
 * a function here, and every function returns a SENTENCE rather than a value or
 * a dash. Pure, so the unit suite can hold each sentence to what it claims.
 */
import type { Requirement, RuleProposal, SourceDocument } from './types.js';

/**
 * The one-line lifecycle verdict for a published requirement.
 *
 * `state` alone is not the answer, and this is the whole reason the console
 * needed a corpus read of its own. A proposal freezes at `published` and never
 * moves; the version it produced goes on to be superseded or withdrawn, and the
 * proposal row still says `published` years later. Read the REQUIREMENT.
 */
export function describeLifecycle(requirement: Requirement): string {
  switch (requirement.state) {
    case 'active':
      return requirement.effectiveTo
        ? `In force, and scheduled to stop applying on ${requirement.effectiveTo}.`
        : 'In force, with no end date. It applies until it is superseded or withdrawn.';
    case 'superseded':
      // `global_knowledge_ended_needs_window_ck` makes a superseded row with an
      // open window unwritable, so `effectiveTo` is never null here. Saying so
      // rather than defaulting: if it IS null the data violated a constraint
      // and the operator should be told that, not shown a blank.
      return requirement.effectiveTo
        ? `Replaced by a newer version. It stopped applying on ${requirement.effectiveTo} and ` +
            'still governs anything dated before that day.'
        : 'Marked superseded with NO END DATE. That state is unwritable through the platform ' +
            'door; this row disagrees with itself and should be reported.';
    case 'retired':
      return requirement.effectiveTo
        ? `Withdrawn by the publisher. It stopped applying on ${requirement.effectiveTo} and ` +
            'still governs anything dated before that day.'
        : 'Marked retired with NO END DATE. That state is unwritable through the platform ' +
            'door; this row disagrees with itself and should be reported.';
  }
}

/**
 * The publisher's own edition label, or the fact that there is not one.
 *
 * "If a source provides no version label, do not invent one — absence is
 * preferable to false precision." (two-authority ruling, 2026-09-07.) The
 * console does not invent one either, and it does not render a blank that an
 * operator could read as an oversight.
 */
export function describeVersionLabel(versionLabel: string | null): string {
  return versionLabel ?? 'The source named no edition. None was invented.';
}

/** The effective window, with each half's absence stated rather than blanked. */
export function describeWindow(from: string | null, to: string | null): string {
  if (from && to) return `${from} to ${to}`;
  if (from) return `${from} onward — no end date recorded`;
  if (to) return `Start date not recorded — ends ${to}`;
  return 'No effective dates recorded on either side.';
}

/**
 * Version history in BOTH directions.
 *
 * `supersedes_id` points only backwards, so "has anything replaced this?" is a
 * question the row cannot answer about itself — the corpus read resolves it
 * forwards. An operator about to supersede a version needs to know something
 * already did.
 */
export function describeSupersession(requirement: Requirement): string {
  const back = requirement.supersedesRuleId
    ? `Supersedes ${requirement.supersedesRuleId}.`
    : 'Supersedes nothing — this is the first published version of this requirement.';
  const forward = requirement.supersededByRuleId
    ? `Superseded by ${requirement.supersededByRuleId}.`
    : 'Nothing has superseded it.';
  return `${back} ${forward}`;
}

/**
 * Retirement attribution, or the honest absence of it.
 *
 * `global_knowledge_retired_is_explicit_ck` holds all three columns together in
 * both directions, so a partial retirement record cannot exist. If one is seen,
 * say so — do not paper over it.
 */
export function describeRetirement(requirement: Requirement): string {
  if (requirement.state !== 'retired') return 'Not withdrawn.';
  const { retiredAt, retiredBy, retirementReason } = requirement;
  if (!retiredAt || !retiredBy || !retirementReason) {
    return 'Retired, but the withdrawal record is incomplete. The database forbids that; report it.';
  }
  return `Withdrawn by ${retiredBy} on ${retiredAt}. Reason: ${retirementReason}`;
}

/**
 * PROVENANCE — who moved this record and when.
 *
 * A missing publication row is not "published by nobody". It is a requirement
 * that predates the platform door, whose provenance this product cannot
 * reconstruct, and that distinction is the difference between a gap in the
 * record and a gap in the process.
 */
export function describeProvenance(requirement: Requirement): string {
  const p = requirement.publication;
  if (!p) {
    return (
      'NO PUBLICATION RECORD. This version predates the platform publication door ' +
      '(migration 1860000000000), so who proposed, approved and published it was never ' +
      'recorded. It is not "published by nobody" — it is provenance this product cannot ' +
      'reconstruct.'
    );
  }
  return (
    `Proposed by ${p.proposedBy} on ${p.proposedAt}; approved by ${p.approvedBy} on ` +
    `${p.approvedAt}; published by ${p.publishedBy} on ${p.publishedAt}. Verified source ` +
    `class at publication: ${p.verifiedSourceClassId}.`
  );
}

/**
 * SOURCE IDENTITY. The declared class and the verified class are separate
 * fields on purpose and the console never shows one in place of the other.
 */
export function describeSourceIdentity(document: SourceDocument): string {
  switch (document.verificationState) {
    case 'unverified':
      return (
        `Declared as "${document.declaredSourceClass}" by ${document.ingestedBy}. ` +
        'NOT YET VERIFIED — nobody has confirmed what this document is, and an unverified ' +
        'document backs no proposal that can publish.'
      );
    case 'rejected':
      return (
        `Declared as "${document.declaredSourceClass}" and REJECTED by ` +
        `${document.verifiedBy ?? 'an unrecorded operator'}` +
        (document.verificationNote ? `: ${document.verificationNote}` : '.')
      );
    case 'verified':
      return document.verifiedSourceClass
        ? `Declared as "${document.declaredSourceClass}"; verified as ` +
            `"${document.verifiedSourceClass}" by ${document.verifiedBy ?? 'an unrecorded operator'}.`
        : 'Marked verified with no confirmed source class. That state is unwritable through ' +
            'the platform door; report it.';
  }
}

/**
 * WHAT PUBLISHING THIS PROPOSAL WILL DO — shown before the operator does it.
 *
 * This is where supersession becomes visible. There is no supersede endpoint;
 * publishing a proposal that carries `supersedesRuleId` IS the supersede, and a
 * console that did not say so would make the most consequential act on this
 * surface look identical to the least.
 */
export function describePublicationEffect(
  proposal: RuleProposal,
  corpus: readonly Requirement[],
): string {
  if (!proposal.supersedesRuleId) {
    return 'Publishing creates a NEW immutable version. It replaces nothing.';
  }
  const predecessor = corpus.find((r) => r.id === proposal.supersedesRuleId);
  if (!predecessor) {
    // Not "supersedes nothing". The console looked and did not find it, which
    // is a different fact and the operator should weigh it.
    return (
      `Publishing SUPERSEDES ${proposal.supersedesRuleId} — a version that is not in the ` +
      'corpus this console loaded. Do not publish until you know what it is.'
    );
  }
  if (predecessor.state !== 'active') {
    return (
      `Publishing SUPERSEDES "${predecessor.title}", which is ALREADY ` +
      `${predecessor.state}. ${describeLifecycle(predecessor)}`
    );
  }
  return (
    `Publishing SUPERSEDES "${predecessor.title}" (${describeWindow(predecessor.effectiveFrom, predecessor.effectiveTo)}) ` +
    'and closes its effective window. The old version stays readable forever, so anything ' +
    'that cited it still resolves to it.'
  );
}

/**
 * Whether the console offers RETIRE on this requirement, and why not when it
 * does not.
 *
 * `retire_global_knowledge_version` refuses a non-active version — "a version
 * leaves force once" — so offering the button would be offering a 422. The
 * server stays the guard; this stops the console lying about what is possible.
 */
export function retirementAvailability(
  requirement: Requirement,
): { available: true } | { available: false; because: string } {
  if (requirement.state === 'active') return { available: true };
  return {
    available: false,
    because:
      `This version is already ${requirement.state}. A version leaves force once, and ` +
      'withdrawal does not delete or rewrite it — the record of what governed stays exactly ' +
      'as it was.',
  };
}

/**
 * Whether the console offers PUBLISH on this proposal, and why not when it
 * does not.
 */
export function publicationAvailability(
  proposal: RuleProposal,
): { available: true } | { available: false; because: string } {
  switch (proposal.state) {
    case 'approved':
      return { available: true };
    case 'proposed':
      return {
        available: false,
        because:
          'Not yet reviewed. A proposal is approved by a second operator before it can publish.',
      };
    case 'published':
      return {
        available: false,
        because: `Already published as ${proposal.publishedRuleId ?? 'a version this console cannot name'}. An approval publishes once.`,
      };
    case 'rejected':
      return {
        available: false,
        because: `Rejected${proposal.reviewNote ? `: ${proposal.reviewNote}` : ' without a recorded reason, which the door forbids.'}`,
      };
    case 'withdrawn':
      return { available: false, because: 'Withdrawn before review.' };
  }
}

/**
 * Whether the console offers REVIEW on this proposal.
 *
 * The two-person rule is the server's (`global_rule_publication_two_person_ck`:
 * `approved_by <> proposed_by`), and it is restated here so a reviewer reading
 * their own proposal is told before they act rather than after.
 */
export function reviewAvailability(
  proposal: RuleProposal,
  operatorId: string,
): { available: true } | { available: false; because: string } {
  if (proposal.state !== 'proposed') {
    return {
      available: false,
      because: `Already ${proposal.state}. A review decision is made once.`,
    };
  }
  if (proposal.proposedBy === operatorId) {
    return {
      available: false,
      because:
        'You extracted this proposal. Approval requires a second operator — the database ' +
        'enforces it (approved_by <> proposed_by) and will refuse the publication either way.',
    };
  }
  return { available: true };
}
